import { findCountry } from '../data/countries';
import { findLanguage, languages } from '../data/languages';

// Browser locale → onboarding defaults. Used by the wizard to pre-fill the
// Country and Language fields so most users hit Continue without typing.

const FALLBACK_LANGUAGE = 'en';

// detectBrowserLanguage returns the first navigator.language(s) entry that
// matches an offered language; falls back to English. Strips the region
// suffix ('sv-SE' → 'sv') because we offer one variant per language.
export function detectBrowserLanguage(): string {
  const candidates = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of candidates) {
    const code = tag.split('-')[0]?.toLowerCase();
    if (code && findLanguage(code)) {
      return code;
    }
  }
  return FALLBACK_LANGUAGE;
}

// detectBrowserCountry parses the region suffix from the browser locale
// (e.g. 'sv-SE' → 'SE') and matches it against the country list. Returns
// empty string if there's no parseable region or it's unknown — the wizard
// then leaves the country field unselected so the user picks explicitly.
export function detectBrowserCountry(): string {
  const candidates = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of candidates) {
    const region = tag.split('-')[1]?.toUpperCase();
    if (region && findCountry(region)) {
      return region;
    }
  }
  return '';
}

// Languages exposed in the wizard, with the detected default surfaced first
// so the eye lands on it.
export function orderedLanguagesForOnboarding(detected: string): readonly { code: string; nativeName: string }[] {
  const ordered = [...languages].sort((a, b) => {
    if (a.code === detected) return -1;
    if (b.code === detected) return 1;
    return a.englishName.localeCompare(b.englishName);
  });
  return ordered.map(({ code, nativeName }) => ({ code, nativeName }));
}

// defaultLanguageForCountry returns the org-language we should pre-pick
// after an address auto-fill resolves a country. Covers the five locales
// we ship; everything else falls back to English so the user still has a
// usable UI. Never empty — the form requires a language code.
export function defaultLanguageForCountry(countryCode: string): string {
  switch (countryCode.toUpperCase()) {
    case 'SE':
      return 'sv';
    case 'NO':
      return 'no';
    case 'DK':
      return 'da';
    case 'FI':
      return 'fi';
    default:
      return 'en';
  }
}
